import { findNearestExchangeRate } from './currency.js';
import { getGeneralLedgerData, getledgerbalance, getledgercount } from "./generalledger.js";

export const unitsofmeasurement = [
  // Weight
  { id: "g", name: "Gram" },
  { id: "kg", name: "Kilogram" },
  { id: "mg", name: "Milligram" },
  { id: "lb", name: "Pound" },
  { id: "oz", name: "Ounce" },

  // Volume
  { id: "ml", name: "Milliliter" },
  { id: "l", name: "Liter" },

  // Length
  { id: "mm", name: "Millimeter" },
  { id: "cm", name: "Centimeter" },
  { id: "m", name: "Meter" },
  { id: "in", name: "Inch" },
  { id: "ft", name: "Foot" },

  // Quantity / Count
  { id: "pcs", name: "Pcs" },
  { id: "dz", name: "Dozen" },
  { id: "pk", name: "Pack" },
  { id: "box", name: "Box" },
  { id: "set", name: "Set" },
  { id: "unit", name: "Unit" },

  // Area
  { id: "sqm", name: "Square Meter" },
  { id: "sqft", name: "Square Foot" },

  // Time
  { id: "hr", name: "Hour" },
  { id: "day", name: "Day" },
  { id: "month", name: "Month" }
];

export const itemtypes = [
  { id: 'inventory', name: 'Inventory' },
  { id: 'service', name: 'Service' }
]

const conversionRates = {
  g: { base: 'kg', factor: 0.001 },
  kg: { base: 'kg', factor: 1 },
  lb: { base: 'kg', factor: 0.453592 },
  mg: { base: 'kg', factor: 0.000001 },
  l: { base: 'l', factor: 1 },
  ml: { base: 'l', factor: 0.001 },
};

function convertUnit(value, from, to) {
  console.log('converting ' + value + ' from ' + from + ' to ' + to)
  if (!value || from === to) return value;

  const fromUnit = conversionRates[from];
  const toUnit = conversionRates[to];

  if (!fromUnit || !toUnit) {
    console.log(`Unsupported unit: ${from} or ${to}`);
    return value;
  }

  if (fromUnit.base !== toUnit.base) {
    console.log(`Incompatible units: ${from} and ${to}`);
    return value;
  }

  const inBase = value * fromUnit.factor;
  return inBase / toUnit.factor;
}

export function getQuantity(context, itemid) {
  const {
    purchaseinvoices,
    salesinvoices,
    productionorders
  } = context;

  let totalQuantityPurchased = 0;
  let totalQuantitySold = 0;

  purchaseinvoices.forEach(invoice => {
    if (invoice.items.some(item => item.accountid === itemid)) {
      invoice.items.forEach(item => {
        if (item.accountid === itemid) {
          totalQuantityPurchased += parseFloat(item.itemquantity || 0);
        }
      });
    }
  });

  salesinvoices.forEach(invoice => {
    invoice.items.forEach(item => {
      if (item.accountid === itemid) {
        totalQuantitySold += parseFloat(item.itemquantity || 0);
      }
    });
  });

  productionorders.forEach(order => {
    order.items.forEach(orderitem => {
      if (orderitem.accountid == itemid) {
        salesinvoices.forEach(invoice => {
          invoice.items.forEach(item => {
            if (item.accountid === order.itemid) {
              totalQuantitySold += orderitem.itemquantity * parseFloat(item.itemquantity || 0);
            }
          });
        });
      }
    });
  });

  const actualQuantity = totalQuantityPurchased - totalQuantitySold;

  return actualQuantity;
}

export function getItemCost(accountid, purchaseinvoices, exchangerates, itemuom, basecurrency) {
  let totalcost = 0;
  let totalquantity = 0;

  purchaseinvoices.forEach(invoice => {
    invoice.items.forEach(invoiceitem => {
      if (invoiceitem.accountid === accountid) {

        let quantity = 1;
        let cost = parseFloat(invoiceitem.itemcost) || 0;

        if (invoiceitem.itemuom !== itemuom) {
          quantity = convertUnit(quantity, invoiceitem.itemuom, itemuom);
        }

        if (invoiceitem.currencyid === basecurrency) {
          totalcost += cost;
        }
        else {
          const NearestExchangeRate = findNearestExchangeRate(exchangerates, invoice.ts, invoice.currencyid, basecurrency);
          const exchangerate = NearestExchangeRate.exchangerate;
          totalcost += (cost * exchangerate);
        }
        totalquantity += quantity;
      }
    });
  });

  if (totalquantity === 0) return 0;
  let itemcost = totalcost / totalquantity;

  return itemcost;
}

export function getProductionCost(accountid, purchaseinvoices, productionorders, exchangerates, basecurrency) {
  let totalcost = 0;

  productionorders
    .filter((account) => account.itemid === accountid)
    .forEach(order => {
      order.items.forEach(orderitem => {
        let itemcost = getItemCost(orderitem.accountid, purchaseinvoices, exchangerates, orderitem.itemuom, basecurrency)
        const quantity = parseFloat(orderitem.itemquantity) || 0;
        totalcost += itemcost * quantity;
      });
    });

  return totalcost;
}

export function getOrderCost(order = [], purchaseinvoices, productionorders, exchangerates, basecurrency) {
  let ordercost = 0;

  order.forEach(item => {
    const cost = getProductionCost(item.id, purchaseinvoices, productionorders, exchangerates, basecurrency);
    const quantity = parseFloat(item.itemquantity) || 0;
    ordercost += cost * quantity;
  });

  return ordercost;
}

export function getItemPrice(items, itemid, exchangerates, basecurrency) {
  const item = items.find(i => i.id === itemid);
  if (!item || !item.pricing || item.pricing.length === 0) return 0;
  const priceInfo = item.pricing[0];
  let price = parseFloat(priceInfo.priceamount) || 0;
  const currencyid = priceInfo.pricecurrencyid;

  if (currencyid !== basecurrency) {
    const NearestExchangeRate = findNearestExchangeRate(exchangerates, Math.floor(Date.now() / 1000), currencyid, basecurrency);
    const exchangerate = NearestExchangeRate.exchangerate;
    price *= exchangerate;
  }

  return price;
}

export function getOrderPrice(order = [], exchangerates, basecurrency) {
  if (order.length == 0) return 0;
  let totalprice = 0;

  order.forEach(orderitem => {
    let price = parseFloat(orderitem.itemprice) || 0;
    const quantity = parseFloat(orderitem.itemquantity) || 0;

    if (orderitem.currencyid != basecurrency) {
      const NearestExchangeRate = findNearestExchangeRate(exchangerates, orderitem.ts, orderitem.currencyid, basecurrency);
      const exchangerate = NearestExchangeRate.exchangerate;
      price = (price * exchangerate);
    }

    totalprice += price * quantity;
  });

  return totalprice;
}

export function getItemAccounts(context) { //20250213
  const {
    items,
    currencies,
    exchangerates,
    basecurrency
  } = context;

  const generalledgerdata = getGeneralLedgerData(context);
  let filteredledgerdata = generalledgerdata
    .filter(data => data.accounttype == 'items')

  const data = [];

  let currencysymbol = currencies.find(curr => curr.id == basecurrency)?.symbol

  items.forEach((account) => {
    let accountledger = filteredledgerdata
      .filter(data => data.accountid == account.id)

    data.push({
      ...account,
      currencyid: basecurrency,
      currencysymbol: currencysymbol,
      balancequantity: getledgercount(accountledger),
      balance: getledgerbalance(accountledger, exchangerates, basecurrency, 'items'),
    });
  });

  data.sort((a, b) => {
    const nameComparison = a.name.localeCompare(b.name, 'en', { numeric: true });
    if (nameComparison === 0) {
      return parseFloat(a.balance) - parseFloat(b.balance);
    }
    return nameComparison;
  });
  console.log(data)
  return data;
}